// v3.18-1328 — Stacked Financing Package email HTML builder.
//
// Renders a professional multi-offer financing proposal that an agent
// receives after an operator clicks "Send Combined Offers to Agent".
// Reuses the existing single-offer email data contract while lifting
// it into a proper HTML card layout.  The template is future-proof
// (renders one card per offer regardless of count) and STRICTLY
// provider-neutral — no lender names, IDs, logos, phones, emails,
// portal URLs, credentials, or any other lender identifier ever
// reaches the outbound HTML.
//
// Internal Average Interest Rate is deliberately EXCLUDED from the
// outbound because these emails go to external agents.  The rate is
// visible to internal staff only inside the Hub UI.
//
// Style choices:
//   * inline CSS only (Gmail/Outlook safe)
//   * -apple-system / Segoe UI / Roboto / Arial fallback
//   * cards use rounded borders + subtle shadow via border-only fallback
//   * PTC dark-navy header, warm-gold accent for the summary
//   * max-width 640px, single-column, mobile-friendly

const _fmtMoney = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(v));
};

const _fmtPct = (v) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
};

const _fmtTerm = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `${n} months` : String(v);
};

const _esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const DEFAULT_INTRO =
  'This client has been approved for a stacked financing package. A stacked ' +
  'financing package combines multiple loan approvals to achieve the total ' +
  'amount requested by the client. Please review each approval below.';

const DEFAULT_NOTES =
  'Please review this financing package with the client before moving forward. ' +
  'This package combines multiple financing approvals into one complete funding solution.';

export const STACKED_EMAIL_DEFAULT_INTRO = DEFAULT_INTRO;
export const STACKED_EMAIL_DEFAULT_NOTES = DEFAULT_NOTES;

/**
 * Deterministically build the Stacked Financing Package HTML.
 * @param {Object}  args
 * @param {Object}  args.client
 * @param {Array<{offerNumber:number, approved_amount:*, payment:*, rate:*, term:*}>} args.offers
 * @param {string=} args.intro         Optional operator-editable intro text.
 * @param {string=} args.notes         Optional operator-editable closing notes.
 * @param {string=} args.company_name  Header brand line, default "Prestigious Travel Club".
 * @param {string=} args.package_title Default "Stacked Financing Package".
 */
export function buildStackedFinancingEmailHTML({
  client,
  offers,
  intro = DEFAULT_INTRO,
  notes = DEFAULT_NOTES,
  company_name = 'Prestigious Travel Club',
  package_title = 'Stacked Financing Package',
}) {
  const clientName  = _esc(client?.full_name || '');
  const clientPhone = _esc(client?.phone || '');
  const list = Array.isArray(offers) ? offers.filter(Boolean) : [];

  const combined = list.reduce(
    (acc, o) => ({
      approved: acc.approved + (Number(o.approved_amount) || 0),
      payment:  acc.payment  + (Number(o.payment) || 0),
    }),
    { approved: 0, payment: 0 }
  );

  // Individual loan card — deliberately never includes lender fields.
  const _card = (o, idx) => `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0"
           style="width:100%; margin:0 0 16px 0; border:1px solid #d1d5db;
                  border-radius:8px; background:#ffffff;">
      <tr>
        <td style="padding:14px 20px; border-bottom:1px solid #e5e7eb;
                   background:#f8fafc;">
          <div style="font-size:11px; letter-spacing:0.14em;
                      text-transform:uppercase; color:#64748b; font-weight:600;">
            Loan Offer ${idx + 1}
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding:16px 20px;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0"
                 style="width:100%; font-size:14px; line-height:1.55; color:#1f2937;">
            <tr>
              <td style="padding:6px 0; color:#6b7280; width:44%;">Approved Amount</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:700; text-align:right;">
                ${_fmtMoney(o.approved_amount)}
              </td>
            </tr>
            <tr>
              <td style="padding:6px 0; color:#6b7280;">Monthly Payment</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:600; text-align:right;">
                ${_fmtMoney(o.payment)}${o.payment ? ' / mo' : ''}
              </td>
            </tr>
            <tr>
              <td style="padding:6px 0; color:#6b7280;">Loan Term</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:600; text-align:right;">
                ${_esc(_fmtTerm(o.term))}
              </td>
            </tr>
            <tr>
              <td style="padding:6px 0; color:#6b7280;">Interest Rate</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:600; text-align:right;">
                ${_fmtPct(o.rate)}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;

  const cards = list.map((o, i) => _card(o, i)).join('');

  const summary = `
    <table role="presentation" cellpadding="0" cellspacing="0" border="0"
           style="width:100%; margin:0 0 16px 0; border:1px solid #fcd34d;
                  border-radius:8px; background:#fffbeb;">
      <tr>
        <td style="padding:14px 20px; border-bottom:1px solid #fde68a;
                   background:#fef3c7;">
          <div style="font-size:11px; letter-spacing:0.14em;
                      text-transform:uppercase; color:#92400e; font-weight:700;">
            Stacked Financing Package
          </div>
          <div style="margin-top:4px; font-size:18px; color:#78350f;
                      font-weight:700; letter-spacing:-0.01em;">
            Combined Package Summary
          </div>
        </td>
      </tr>
      <tr>
        <td style="padding:16px 20px;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0"
                 style="width:100%; font-size:14px; line-height:1.55; color:#1f2937;">
            <tr>
              <td style="padding:6px 0; color:#6b7280; width:52%;">Combined Approved Amount</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:700;
                         font-size:18px; text-align:right;">
                ${_fmtMoney(combined.approved)}
              </td>
            </tr>
            <tr>
              <td style="padding:6px 0; color:#6b7280;">Combined Monthly Payment</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:700;
                         font-size:16px; text-align:right;">
                ${_fmtMoney(combined.payment)}${combined.payment ? ' / mo' : ''}
              </td>
            </tr>
            <tr>
              <td style="padding:6px 0; color:#6b7280;">Number of Loans Included</td>
              <td style="padding:6px 0; color:#0f172a; font-weight:700; text-align:right;">
                ${list.length}
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;

  const safeIntro = _esc(intro).replace(/\n/g, '<br>');
  const safeNotes = _esc(notes).replace(/\n/g, '<br>');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${_esc(package_title)}</title>
</head>
<body style="margin:0; padding:0; background:#f1f5f9;
             font-family:-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
             color:#1f2937;">
  <center style="width:100%; background:#f1f5f9; padding:24px 12px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0"
           style="width:100%; max-width:640px; background:#ffffff;
                  border-radius:12px; overflow:hidden;
                  border:1px solid #e2e8f0;">
      <!-- Brand header -->
      <tr>
        <td style="padding:24px 24px 20px 24px; background:#0f172a;
                   color:#f8fafc; text-align:left;">
          <div style="font-size:11px; letter-spacing:0.24em;
                      text-transform:uppercase; color:#cbd5e1; font-weight:600;">
            ${_esc(company_name)}
          </div>
          <div style="margin-top:6px; font-size:22px; font-weight:700;
                      letter-spacing:-0.02em; color:#f8fafc;">
            ${_esc(package_title)}
          </div>
        </td>
      </tr>
      <!-- Client intro -->
      <tr>
        <td style="padding:20px 24px 4px 24px;">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0"
                 style="width:100%; font-size:14px; line-height:1.6; color:#1f2937;">
            <tr>
              <td style="padding:4px 0; color:#6b7280; width:130px;">Client Name</td>
              <td style="padding:4px 0; color:#0f172a; font-weight:600;">${clientName || '—'}</td>
            </tr>
            <tr>
              <td style="padding:4px 0; color:#6b7280;">Phone</td>
              <td style="padding:4px 0; color:#0f172a; font-weight:600;">${clientPhone || '—'}</td>
            </tr>
          </table>
          <p style="margin:16px 0 20px 0; font-size:14px; line-height:1.6;
                    color:#334155;">${safeIntro}</p>
        </td>
      </tr>
      <!-- Loan offers + summary -->
      <tr>
        <td style="padding:0 24px 4px 24px;">
          ${cards}
          ${summary}
        </td>
      </tr>
      <!-- Notes / closing -->
      <tr>
        <td style="padding:8px 24px 24px 24px;">
          <div style="padding:14px 16px; border-left:4px solid #0891b2;
                      background:#ecfeff; border-radius:4px;">
            <p style="margin:0; font-size:13px; line-height:1.55; color:#155e75;">
              ${safeNotes}
            </p>
          </div>
        </td>
      </tr>
      <!-- Footer -->
      <tr>
        <td style="padding:16px 24px 24px 24px; text-align:center;
                   background:#f8fafc; border-top:1px solid #e2e8f0;">
          <div style="font-size:11px; color:#64748b; letter-spacing:0.08em;
                      text-transform:uppercase; font-weight:600;">
            ${_esc(company_name)}
          </div>
          <div style="margin-top:4px; font-size:11px; color:#94a3b8;">
            This financing package summary is confidential and intended for the
            recipient agent only.
          </div>
        </td>
      </tr>
    </table>
  </center>
</body>
</html>`;
}


export function buildStackedFinancingEmailSubject(client) {
  const name = client?.full_name || 'Client';
  return `Stacked Financing Package for ${name}`;
}
